import { Injectable, signal } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { firstValueFrom } from 'rxjs';
import { Calibration } from '../entities/calibration';
import { CalibrationResource } from '../entities/calibration-resource';
import { UserAuth } from '../entities/user-auth';
import { EventLogEntityType } from '../entities/event-log';
import { Nozzle } from '../entities/nozzle';
import { PendingIncompleteCalibration } from '../entities/pending-incomplete-calibration';
import { Reason } from '../entities/reason';
import { Region } from '../entities/region';
import { ServiceOrder } from '../entities/service-order';
import { Vendor } from '../entities/vendor';
import { CalibrationRepository } from '../local-db/repositories/calibration.repository';
import { NozzleRepository } from '../local-db/repositories/nozzle.repository';
import { PendingIncompleteCalibrationRepository } from '../local-db/repositories/pending-incomplete-calibration.repository';
import { ReasonRepository } from '../local-db/repositories/reason.repository';
import { RegionRepository } from '../local-db/repositories/region.repository';
import { ServiceOrderRepository } from '../local-db/repositories/service-order.repository';
import { SynchronizationRepository } from '../local-db/repositories/synchronization.repository';
import { SynchronizationCheckpointRepository } from '../local-db/repositories/synchronization-checkpoint.repository';
import { UserRepository } from '../local-db/repositories/user.repository';
import { VendorRepository } from '../local-db/repositories/vendor.repository';
import { ConnectionService } from './connection.service';
import {
  SynchronizationStep,
  SynchronizationStepDefinition,
} from './dtos/synchronization-step.dto';
import {
  CalibrationInputDto,
  CalibrationOutputDto,
  CalibrationService,
  CalibrationStatus,
} from 'src/app/proxy/calibrations';
import { NozzleService } from 'src/app/proxy/nozzles';
import { ReasonService } from 'src/app/proxy/reasons';
import { RegionService } from 'src/app/proxy/regions';
import { ServiceOrderService } from 'src/app/proxy/service-orders';
import { UserService } from 'src/app/proxy/users';
import { VendorService } from 'src/app/proxy/vendors';
import { User } from '../entities/user';
import { SynchronizationCheckpoint } from '../entities/synchronization-checkpoint';
import { HashService } from './hash.service';
import { ToastService } from './toast.service';
import { EventLogService } from './event-log.service';
import {
  analyzeCalibrationSubmissionError
} from '../utils/calibration-submission-error.utils';

type SyncAllOptions = {
  showAlreadyInProgressWarning?: boolean;
};

@Injectable({ providedIn: 'root' })
export class SynchronizationManagerService {
  readonly syncSteps = signal<SynchronizationStep[]>([]);
  readonly isSyncInProgress = signal(false);

  private readonly stepDefinitions: ReadonlyArray<SynchronizationStepDefinition> = [
    { key: 'user', localizationKey: '::User' },
    { key: 'calibrationPublication', localizationKey: '::Synchronization:CalibrationPublication' },
    { key: 'calibrationFetch', localizationKey: '::Synchronization:CalibrationFetch' },
    { key: 'users', localizationKey: '::Users' },
    { key: 'regions', localizationKey: '::Regions' },
    { key: 'vendors', localizationKey: '::Vendors' },
    { key: 'nozzles', localizationKey: '::Nozzles' },
    { key: 'reasons', localizationKey: '::Reasons' },
    { key: 'serviceOrders', localizationKey: '::ServiceOrders' },
  ];

  private readonly stepEntityMap: Readonly<Record<string, EventLogEntityType>> = {
    user: 'user',
    calibrationPublication: 'calibrations',
    calibrationFetch: 'calibrations',
    users: 'users',
    regions: 'regions',
    vendors: 'vendors',
    nozzles: 'nozzles',
    reasons: 'reasons',
    serviceOrders: 'serviceOrders',
  };

  constructor(
    private readonly userService: UserService,
    private readonly regionService: RegionService,
    private readonly vendorService: VendorService,
    private readonly nozzleService: NozzleService,
    private readonly reasonService: ReasonService,
    private readonly serviceOrderService: ServiceOrderService,
    private readonly calibrationService: CalibrationService,
    private readonly connectionService: ConnectionService,
    private readonly regionRepository: RegionRepository,
    private readonly vendorRepository: VendorRepository,
    private readonly nozzleRepository: NozzleRepository,
    private readonly reasonRepository: ReasonRepository,
    private readonly pendingIncompleteCalibrationRepository: PendingIncompleteCalibrationRepository,
    private readonly serviceOrderRepository: ServiceOrderRepository,
    private readonly calibrationRepository: CalibrationRepository,
    private readonly synchronizationRepository: SynchronizationRepository,
    private readonly synchronizationCheckpointRepository: SynchronizationCheckpointRepository,
    private readonly userRepository: UserRepository,
    private readonly hashService: HashService,
    private readonly toastService: ToastService,
    private readonly localization: LocalizationService,
    private readonly eventLogService: EventLogService
  ) {}

  async getLastSynchronizationSteps(): Promise<SynchronizationStep[]> {
    const results = await this.synchronizationRepository.getAll();

    return this.stepDefinitions.map(definition => {
      const result = results.find(item => item.itemType === definition.key);
      return {
        key: definition.key,
        label: this.getLocalizedStepLabel(definition),
        status: result ? 'success' : 'pending',
        synchronizedRecords: result?.itemsCount ?? 0,
      };
    });
  }

  async getLastSyncAt(): Promise<string | undefined> {
    return this.synchronizationRepository.getLastSyncAt();
  }

  async restorePendingSynchronization(email?: string): Promise<void> {
    if (this.isSyncInProgress()) {
      return;
    }

    if (!email) {
      this.clearRuntimeState();
      return;
    }

    const checkpoint = await this.getPendingCheckpoint(email);
    if (!checkpoint) {
      this.clearRuntimeState();
      return;
    }

    this.syncSteps.set(this.buildSynchronizationSteps(checkpoint));
  }

  async getPendingCheckpoint(email: string): Promise<SynchronizationCheckpoint | undefined> {
    const checkpoint = await this.synchronizationCheckpointRepository.getByUserEmail(email);
    if (!checkpoint) {
      return undefined;
    }

    if (checkpoint.steps.length === 0 || this.areAllStepsCompleted(checkpoint.steps)) {
      await this.synchronizationCheckpointRepository.deleteForUser(email);
      return undefined;
    }

    const lastSyncAt = await this.synchronizationRepository.getLastSyncAt();
    if (!lastSyncAt) {
      return checkpoint;
    }

    const shouldResume =
      new Date(checkpoint.updatedAt).getTime() > new Date(lastSyncAt).getTime();

    if (!shouldResume) {
      await this.synchronizationCheckpointRepository.deleteForUser(email);
      return undefined;
    }

    return checkpoint;
  }

  async syncAll(email: string, options?: SyncAllOptions): Promise<void> {
    const showAlreadyInProgressWarning = options?.showAlreadyInProgressWarning ?? true;

    if (this.isSyncInProgress()) {
      if (showAlreadyInProgressWarning) {
        this.toastService.showWarningMessage(
          this.localization.instant('::Synchronization:AlreadyInProgress')
        );
      }
      return;
    }

    if (!email) {
      this.toastService.showWarningMessage(this.localization.instant('::Synchronization:EmailRequired'));
      return;
    }

    const checkpoint = await this.getPendingCheckpoint(email);

    await this.initializeSynchronization(email, checkpoint);
    this.isSyncInProgress.set(true);

    try {
      const failedSteps: string[] = [];
      let vendorId: number | undefined;

      await this.runStepIfNeeded(email, 'user', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.userService.getByEmail(email, { skipHandleError: true })
        );

        if (!syncResult?.id) {
          return 0;
        }

        const user = new UserAuth({
          id: syncResult.id,
          email: syncResult.email ?? email,
          username: syncResult.username,
          vendorId: syncResult.vendorId,
        });

        await this.userRepository.saveCurrent(user);
        await this.synchronizationRepository.saveStepResult('user', 1);
        vendorId = syncResult.vendorId ?? undefined;
        return 1;
      });

      await this.runStepIfNeeded(email, 'calibrationPublication', failedSteps, async () => {
        return this.syncPendingPublishes();
      });

      await this.runStepIfNeeded(email, 'calibrationFetch', failedSteps, async () => {
        vendorId = await this.resolveVendorId(vendorId);

        const syncResult = await firstValueFrom(
          this.calibrationService.getByVendor(vendorId, { skipHandleError: true })
        );

        const calibrations = syncResult.map(dto => this.mapBackendCalibration(dto));
        const synchronizedRecords = await this.calibrationRepository.syncFromBackend(calibrations);

        await this.synchronizationRepository.saveStepResult(
          'calibrationFetch',
          synchronizedRecords
        );

        return synchronizedRecords;
      });

      await this.runStepIfNeeded(email, 'users', failedSteps, async () => {
        vendorId = await this.resolveVendorId(vendorId);

        const syncResult = await firstValueFrom(
          this.userService.getByVendor(vendorId, { skipHandleError: true })
        );

        const users = await Promise.all(
          syncResult
            .filter(dto => !!dto.username)
            .map(async dto => {
              return new User({
                username: dto.username,
                vendorId: dto.vendorId,
                qrCode: await this.hashService.hash(dto.qrCode),
                pin: await this.hashService.hash(dto.pin.toString()),
              });
            })
        );

        const usersByUsername = new Map(
          users
            .filter(user => !!user.username?.trim())
            .map(user => [user.username!.trim(), user])
        );

        const vendorUsers = Array.from(usersByUsername.values());
        await this.userRepository.replaceAll(vendorUsers);
        await this.synchronizationRepository.saveStepResult('users', vendorUsers.length);
        return vendorUsers.length;
      });

      await this.runStepIfNeeded(email, 'regions', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.regionService.getAll({ skipHandleError: true })
        );

        const regions = syncResult.map(dto => new Region(dto));
        await this.regionRepository.replaceAll(regions);
        await this.synchronizationRepository.saveStepResult('regions', regions.length);
        return regions.length;
      });

      await this.runStepIfNeeded(email, 'vendors', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.vendorService.getAll({ skipHandleError: true })
        );

        const vendors = syncResult.map(dto => new Vendor(dto));
        await this.vendorRepository.replaceAll(vendors);
        await this.synchronizationRepository.saveStepResult('vendors', vendors.length);
        return vendors.length;
      });

      await this.runStepIfNeeded(email, 'nozzles', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.nozzleService.getAll({ skipHandleError: true })
        );

        const nozzles = syncResult.map(dto => new Nozzle(dto));
        await this.nozzleRepository.replaceAll(nozzles);
        await this.synchronizationRepository.saveStepResult('nozzles', nozzles.length);
        return nozzles.length;
      });

      await this.runStepIfNeeded(email, 'reasons', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.reasonService.getAll({ skipHandleError: true })
        );

        const reasons = syncResult.map(dto => new Reason(dto));
        await this.reasonRepository.replaceAll(reasons);
        await this.synchronizationRepository.saveStepResult('reasons', reasons.length);
        return reasons.length;
      });

      await this.runStepIfNeeded(email, 'serviceOrders', failedSteps, async () => {
        vendorId = await this.resolveVendorId(vendorId);

        const syncResult = await firstValueFrom(
          this.serviceOrderService.getByVendor(vendorId, {
            skipHandleError: true,
          })
        );

        const serviceOrders = syncResult.map(dto => new ServiceOrder(dto));
        await this.serviceOrderRepository.replaceAll(serviceOrders);
        await this.synchronizationRepository.saveStepResult('serviceOrders', serviceOrders.length);
        return serviceOrders.length;
      });

      await this.synchronizationRepository.saveSummary();

      if (this.areAllStepsCompleted(this.syncSteps())) {
        await this.synchronizationCheckpointRepository.deleteForUser(email);
      }

      if (failedSteps.length > 0) {
        this.toastService.showWarningMessage(
          this.localization.instant(
            '::Synchronization:CompletedWithErrorsForSteps',
            failedSteps.map(stepName => this.getStepLabelByKey(stepName)).join(', ')
          )
        );
      }
    } finally {
      this.isSyncInProgress.set(false);
    }
  }

  private async syncPendingPublishes(): Promise<number> {
    const pendingItems = await this.calibrationRepository.getUnpublished();
    const pendingIncompleteItems = await this.pendingIncompleteCalibrationRepository.getAll();

    for (const calibration of pendingItems) {
      await this.sendPendingPublish(calibration.calibrationId);
    }

    for (const incompleteCalibration of pendingIncompleteItems) {
      await this.sendPendingIncompletePublish(incompleteCalibration);
    }

    const synchronizedRecords = pendingItems.length + pendingIncompleteItems.length;
    await this.synchronizationRepository.saveStepResult(
      'calibrationPublication',
      synchronizedRecords
    );

    return synchronizedRecords;
  }

  private async sendPendingPublish(calibrationId: string): Promise<void> {
    if (!this.connectionService.online()) {
      await this.eventLogService.log({
        type: 'pending-calibration-submitted',
        status: 'warning',
        relatedEntityType: 'calibration',
        relatedEntityId: calibrationId,
        message: this.localization.instant('::Synchronization:UnableToPostPending'),
      });
      this.toastService.showWarningMessage(
        this.localization.instant('::Synchronization:UnableToPostPending')
      );
      return;
    }

    try {
      const calibration = await this.calibrationRepository.getById(calibrationId);
      if (!calibration) {
        return;
      }

      const { usesRuntime, ...restCalibration } = calibration;
      const calibrationInput: CalibrationInputDto = {
        ...restCalibration,
        resources: restCalibration.resources.map(({ id, calibrationId: _, ...resource }) => resource),
      };
      const calibrationOutput: CalibrationOutputDto = await firstValueFrom(
        this.calibrationService.submit(calibrationInput, { skipHandleError: true })
      );
      const published = calibrationOutput.published ?? calibrationOutput.status === CalibrationStatus.Published;
      const updates: Partial<Calibration> = {
        status: calibrationOutput.status,
      };

      if (calibrationOutput.published !== undefined) {
        updates.published = calibrationOutput.published;
      }

      if (calibrationOutput.publishedDate !== undefined) {
        updates.publishedDate = calibrationOutput.publishedDate;
      }

      await this.calibrationRepository.update(calibrationId, updates);
    } catch (error) {
      const errorAnalysis = analyzeCalibrationSubmissionError(error);

      await this.eventLogService.log({
        type: 'pending-calibration-submitted',
        status: 'error',
        relatedEntityType: 'calibration',
        relatedEntityId: calibrationId,
        message: this.localization.instant('::SendCalibration:UnableToSend'),
        errorMessage: errorAnalysis.errorMessage,
      });
    }
  }

  private async sendPendingIncompletePublish(
    incompleteCalibration: PendingIncompleteCalibration
  ): Promise<void> {
    if (!this.connectionService.online()) {
      await this.eventLogService.log({
        type: 'incomplete-calibration-submitted',
        status: 'warning',
        relatedEntityType: 'incompleteCalibration',
        relatedEntityId: incompleteCalibration.calibrationId,
        message: this.localization.instant('::Synchronization:UnableToPostPending'),
      });
      this.toastService.showWarningMessage(
        this.localization.instant('::Synchronization:UnableToPostPending')
      );
      return;
    }

    try {
      await firstValueFrom(
        this.calibrationService.submitIncomplete(incompleteCalibration, { skipHandleError: true })
      );

      await this.pendingIncompleteCalibrationRepository.delete(incompleteCalibration.calibrationId);
      await this.calibrationRepository.delete(incompleteCalibration.calibrationId);
      await this.eventLogService.log({
        type: 'incomplete-calibration-submitted',
        status: 'success',
        relatedEntityType: 'incompleteCalibration',
        relatedEntityId: incompleteCalibration.calibrationId,
        message: this.localization.instant('::Calibrations:IncompleteSaved'),
      });
    } catch (error) {
      const errorAnalysis = analyzeCalibrationSubmissionError(error);

      await this.eventLogService.log({
        type: 'incomplete-calibration-submitted',
        status: 'error',
        relatedEntityType: 'incompleteCalibration',
        relatedEntityId: incompleteCalibration.calibrationId,
        message: this.localization.instant('::Calibrations:IncompleteSubmitFailed'),
        errorMessage: errorAnalysis.errorMessage,
      });
    }
  }

  private async runSyncStep(
    email: string,
    stepName: string,
    failedSteps: string[],
    action: () => Promise<number>
  ): Promise<void> {
    this.updateStep(stepName, { status: 'running' });
    await this.saveCheckpoint(email);

    try {
      const synchronizedRecords = await action();
      this.updateStep(stepName, { status: 'success', synchronizedRecords });
      await this.eventLogService.log({
        type: 'synchronization',
        status: 'success',
        relatedEntityType: this.mapStepToEventEntity(stepName),
        recordsCount: synchronizedRecords,
      });
    } catch (error) {
      failedSteps.push(stepName);
      const errorMessage = error instanceof Error ? error.message : undefined;
      this.updateStep(stepName, { status: 'error', errorMessage });
      await this.eventLogService.log({
        type: 'synchronization',
        status: 'error',
        relatedEntityType: this.mapStepToEventEntity(stepName),
        recordsCount: 0,
        errorMessage,
      });
    }

    await this.saveCheckpoint(email);
  }

  private async runStepIfNeeded(
    email: string,
    stepName: string,
    failedSteps: string[],
    action: () => Promise<number>
  ): Promise<void> {
    const step = this.syncSteps().find(item => item.key === stepName);
    if (step && this.isCompletedStep(step.status)) {
      return;
    }

    await this.runSyncStep(email, stepName, failedSteps, action);
  }

  private async initializeSynchronization(
    email: string,
    checkpoint?: SynchronizationCheckpoint
  ): Promise<void> {
    const startedAt = checkpoint?.startedAt ?? new Date().toISOString();
    this.syncSteps.set(this.buildSynchronizationSteps(checkpoint));

    await this.saveCheckpoint(email, startedAt);
  }

  private async saveCheckpoint(email: string, startedAt?: string): Promise<void> {
    const current = await this.synchronizationCheckpointRepository.getByUserEmail(email);
    const now = new Date().toISOString();

    await this.synchronizationCheckpointRepository.save(
      new SynchronizationCheckpoint({
        userEmail: email,
        startedAt: startedAt ?? current?.startedAt ?? now,
        updatedAt: now,
        steps: this.syncSteps().map(step => ({ ...step })),
      })
    );
  }

  private updateStep(stepName: string, partialStep: Partial<SynchronizationStep>): void {
    this.syncSteps.update(steps =>
      steps.map(step => (step.key === stepName ? { ...step, ...partialStep } : step))
    );
  }

  private getLocalizedStepLabel(step: SynchronizationStepDefinition): string {
    return this.localization.instant(step.localizationKey);
  }

  private getStepLabelByKey(stepName: string): string {
    const stepDefinition = this.stepDefinitions.find(step => step.key === stepName);
    return stepDefinition ? this.getLocalizedStepLabel(stepDefinition) : stepName;
  }

  private async resolveVendorId(vendorId?: number): Promise<number> {
    if (vendorId != null) {
      return vendorId;
    }

    const stored = await this.userRepository.getCurrent();
    const resolvedVendorId = stored?.vendorId;

    if (resolvedVendorId == null) {
      throw new Error(this.localization.instant('::Synchronization:VendorRequired'));
    }

    return resolvedVendorId;
  }

  private isCompletedStep(status: SynchronizationStep['status']): boolean {
    return status === 'success' || status === 'error';
  }

  private areAllStepsCompleted(steps: SynchronizationStep[]): boolean {
    return steps.every(step => this.isCompletedStep(step.status));
  }

  private clearRuntimeState(): void {
    this.syncSteps.set([]);
  }

  private mapBackendCalibration(dto: CalibrationOutputDto): Calibration {
    return new Calibration({
      ...dto,
      resources: (dto.resources ?? []).map(
        resource =>
          new CalibrationResource({
            ...resource,
            calibrationId: dto.calibrationId,
            id: `${dto.calibrationId}-${resource.resourceId}`,
          })
      ),
      published: dto.published ?? dto.status === CalibrationStatus.Published,
      publishedDate: dto.publishedDate ?? null,
      bioforestOriginId: dto.id,
    });
  }

  private mapStepToEventEntity(stepName: string): EventLogEntityType {
    return this.stepEntityMap[stepName] ?? 'calibration';
  }

  private buildSynchronizationSteps(
    checkpoint?: SynchronizationCheckpoint
  ): SynchronizationStep[] {
    const checkpointSteps = new Map((checkpoint?.steps ?? []).map(step => [step.key, step]));

    return this.stepDefinitions.map(definition => {
      const previousStep = checkpointSteps.get(definition.key);

      return previousStep?.status === 'success'
        ? { ...previousStep, label: this.getLocalizedStepLabel(definition), errorMessage: undefined }
        : {
            key: definition.key,
            label: this.getLocalizedStepLabel(definition),
            status: previousStep?.status === 'error' ? 'error' : 'pending',
            synchronizedRecords: previousStep?.synchronizedRecords ?? 0,
            errorMessage: previousStep?.errorMessage,
          };
    });
  }
}
